import { z } from "zod";
import { BASE, ISO, ISO_DATE, MONEY, serverOwned, CUSTOM_VALUES } from "./common.ts";
import { ESIGN_FIELD } from "./esign-fields.ts";

export { ESIGN_FIELD, ESIGN_RESPONSES, type EsignField } from "./esign-fields.ts";

let ISO_CODES: Set<string> | undefined;
/** True for an upper-case ISO 4217 code the runtime knows (e.g. "AUD", "JPY"); the list is Intl's own. */
export function isIsoCurrency(code: string): boolean {
  ISO_CODES ??= new Set(Intl.supportedValuesOf("currency"));
  return ISO_CODES.has(code);
}

export const INVOICE_STATUS = z.enum(["draft", "scheduled", "sent", "viewed", "partial", "paid", "overdue", "void"]);
export const INVOICE = serverOwned(BASE.extend({
  number: z.string(),
  clientId: z.string(),
  projectId: z.string().optional(),
  status: INVOICE_STATUS,
  issued: ISO,
  due: ISO,
  total: MONEY,
  paid: MONEY,
  lines: z.number(),
  po: z.string().optional(),
  remindersSent: z.number(),
  paidOn: ISO.optional(),
  /** X18-I03: the day (YYYY-MM-DD) the client promised to pay by on the pay link; reminders pause until the day after it. */
  promisedOn: ISO_DATE.optional(),
  depositPct: z.number().optional(),
  /** X21-RESB-02: the client may pay part of the invoice now and the rest stays due. Unset means full payment only (as before). */
  allowPartial: z.boolean().optional(),
  /** The accepted proposal this deposit invoice was raised for; the accept workflow looks it up to stay safe to repeat. */
  proposalId: z.string().optional(),
  /** The part of `paid` settled by credit notes rather than money. */
  credited: MONEY.optional(),
  lateFeeBp: z.number().optional(),
  custom: CUSTOM_VALUES.optional(),
  /** Where the invoice came from: the tool it was imported from and its id there (imports only, X4-04). */
  externalRef: z.object({ source: z.string(), id: z.string() }).optional(),
  /** Notes and terms printed under the lines, on the document and on the pay page. */
  notes: z.string().max(2000).optional(),
  /** The brand whose kit prints this invoice's PDF (X11-811a); unset prints in the default brand. */
  brandId: z.string().optional(),
  /** The plan this renewal invoice was raised for (X18-I01a); the Drafts to review list reads drafts that carry it. */
  subscriptionId: z.string().optional(),
  /** The period a renewal invoice bills: from its start to the day before the next renewal (X18-I01a). */
  servicePeriod: z.object({ start: ISO, end: ISO }).optional(),
  /** The consolidated draft this draft was merged into (set on the voided sources, X6-08a). */
  consolidatedInto: z.string().optional(),
  /** The drafts this consolidated draft was built from (X6-08a). */
  consolidatedFrom: z.array(z.string()).optional(),
  /** Practice data for the walkthroughs (T1-05a): set by the server only. */
  practice: z.boolean().optional(),
  /** The access point's id for an invoice delivered over Peppol (X13-0518a): set by the server only. */
  peppolGuid: z.string().max(100).optional(),
}), ["paid", "remindersSent", "credited", "externalRef", "consolidatedInto", "consolidatedFrom", "practice", "peppolGuid"]);
export type Invoice = z.infer<typeof INVOICE>;

export const INVOICE_LINE_KIND = z.enum(["service", "time", "expense", "discount", "fee", "milestone"]);
export type InvoiceLineKind = z.infer<typeof INVOICE_LINE_KIND>;

/** One row of an invoice. `total` is the row amount after quantity; the rows of an invoice sum to its `total`. */
export const INVOICE_LINE = BASE.extend({
  invoiceId: z.string(),
  order: z.number(),
  kind: INVOICE_LINE_KIND,
  refId: z.string().optional(),
  description: z.string(),
  qty: z.number(),
  unit: MONEY,
  taxRuleId: z.string().optional(),
  total: MONEY,
});
export type InvoiceLine = z.infer<typeof INVOICE_LINE>;

/** A tax rate. `rateBp` is the rate in basis points (1000 is 10%); `inclusive` means the price already contains the tax. */
export const TAX_RULE = BASE.extend({
  name: z.string(),
  rateBp: z.number().int(),
  inclusive: z.boolean(),
  region: z.string(),
  /** The codes the accounting sync sends with a line that uses this rule (X6-08b): Xero TaxType, QuickBooks TaxCodeRef. */
  xeroTaxType: z.string().trim().max(40).optional(),
  qboTaxCode: z.string().trim().max(40).optional(),
});
export type TaxRule = z.infer<typeof TAX_RULE>;

export const PROPOSAL_SECTION_KIND = z.enum(["text", "pricing", "packages", "deposit", "terms", "heading", "image", "video", "gallery", "testimonial", "sample-week"]);
export type ProposalSectionKind = z.infer<typeof PROPOSAL_SECTION_KIND>;

/** Sample week (X21-RESB-02): one short text per day, Monday to Sunday. A section's `data` holds scalars only, so each day is its own key. */
export const SAMPLE_WEEK_DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;
export const SAMPLE_WEEK_DATA = z.object({
  mon: z.string().max(500).default(""), tue: z.string().max(500).default(""), wed: z.string().max(500).default(""),
  thu: z.string().max(500).default(""), fri: z.string().max(500).default(""), sat: z.string().max(500).default(""), sun: z.string().max(500).default(""),
});

/** Smart content: a section is shown only when its condition holds. `field` is what is tested (the client's industry, a tag,
 *  a country, the proposal's value, or the package the client picked); `value` is the text or the number it is tested against. */
export const PROPOSAL_CONDITION_FIELD = z.enum(["client.industry", "client.tag", "client.country", "proposal.value", "package.chosen"]);
export const PROPOSAL_CONDITION_OP = z.enum(["eq", "neq", "gt", "lt", "includes"]);
export const PROPOSAL_CONDITION = z.object({ field: PROPOSAL_CONDITION_FIELD, op: PROPOSAL_CONDITION_OP, value: z.union([z.string(), z.number()]) });
export type ProposalCondition = z.infer<typeof PROPOSAL_CONDITION>;

/** A section of a proposal. `data` holds the scalar fields of its kind (body text, deposit percent, package id).
 *  `locked` blocks are changed only by an owner or admin; `views` and `readSeconds` are what the public page reports.
 *  `blockId` links the section to a content-library block: its title and text follow the block while the proposal is a draft.
 *  `condition` is the smart-content rule. Both are saved inside `data` (see sectionDataFor), so the row needs no column. */
export const PROPOSAL_SECTION = BASE.extend({
  proposalId: z.string(),
  order: z.number(),
  kind: PROPOSAL_SECTION_KIND,
  title: z.string(),
  data: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])),
  locked: z.boolean().optional(),
  views: z.number().optional(),
  readSeconds: z.number().optional(),
  blockId: z.string().optional(),
  condition: PROPOSAL_CONDITION.optional(),
});
export type ProposalSection = z.infer<typeof PROPOSAL_SECTION>;

/** The library link a section's stored data holds (`blockId`); an empty value means none. */
export function sectionLinkOf(data: Record<string, string | number | boolean>): string | undefined {
  return typeof data.blockId === "string" && data.blockId !== "" ? data.blockId : undefined;
}

/** The condition a section's stored data holds (`condField`, `condOp`, `condValue`); a missing or unknown field means none. */
export function sectionConditionOf(data: Record<string, string | number | boolean>): ProposalCondition | undefined {
  const parsed = PROPOSAL_CONDITION.safeParse({ field: data.condField, op: data.condOp, value: data.condValue });
  return parsed.success ? parsed.data : undefined;
}

/** The data keys a section saves for its link and condition. An empty value clears it, because a save merges into the stored data. */
export function sectionDataFor(blockId: string | undefined, condition: ProposalCondition | undefined): Record<string, string | number> {
  return { blockId: blockId ?? "", condField: condition?.field ?? "", condOp: condition?.op ?? "", condValue: condition?.value ?? "" };
}

export const RICH_SECTION_KINDS = ["heading", "image", "video", "gallery", "testimonial", "sample-week"] as const;
export type RichSectionKind = (typeof RICH_SECTION_KINDS)[number];
const HTTPS = /^https:\/\/\S+$/;
/** The trimmed address when it is an https address of at most 500 characters, else undefined. */
export function httpsUrlOf(v: unknown): string | undefined {
  return typeof v === "string" && v.trim().length <= 500 && HTTPS.test(v.trim()) ? v.trim() : undefined;
}
export type GalleryImage = { url: string; alt: string };
type SectionData = Record<string, string | number | boolean>;
/** The images of a gallery section (data.images is JSON text): entries without an https address or a description are dropped; at most 12. */
export function galleryImagesOf(data: SectionData): GalleryImage[] {
  if (typeof data.images !== "string") return [];
  try {
    const raw: unknown = JSON.parse(data.images);
    if (!Array.isArray(raw)) return [];
    return raw.flatMap((x: { url?: unknown; alt?: unknown } | null) => {
      const url = httpsUrlOf(x?.url);
      const alt = typeof x?.alt === "string" ? x.alt.trim() : "";
      return url && alt ? [{ url, alt }] : [];
    }).slice(0, 12);
  } catch { return []; }
}
export type RichIssue = { field: string; message: string };
/** What is wrong with a rich section's data, in words for the person editing (empty when it is fine). Same rules on the server and in the editor. */
export function richSectionIssues(kind: string, data: SectionData): RichIssue[] {
  const text = (v: unknown) => (typeof v === "string" ? v.trim() : "");
  const out: RichIssue[] = [];
  if (kind === "heading" && data.level !== undefined && ![1, 2, 3].includes(Number(data.level))) out.push({ field: "level", message: "A heading level is 1, 2 or 3." });
  if (kind === "image") {
    if (!httpsUrlOf(data.url)) out.push({ field: "url", message: "The image needs an https address." });
    if (!text(data.alt) || text(data.alt).length > 200) out.push({ field: "alt", message: "Describe the image for people who cannot see it." });
    if (text(data.caption).length > 300) out.push({ field: "caption", message: "A caption is at most 300 characters." });
  }
  if (kind === "video") {
    if (!httpsUrlOf(data.url)) out.push({ field: "url", message: "The video needs an https address." });
    if (text(data.caption).length > 300) out.push({ field: "caption", message: "A caption is at most 300 characters." });
  }
  if (kind === "gallery") {
    let raw: unknown;
    try { raw = JSON.parse(text(data.images) || "[]"); } catch { return [{ field: "images", message: "The gallery images are not readable." }]; }
    if (!Array.isArray(raw) || raw.length < 1 || raw.length > 12) return [{ field: "images", message: "A gallery holds 1 to 12 images." }];
    raw.forEach((x: { url?: unknown; alt?: unknown } | null, i) => {
      if (!httpsUrlOf(x?.url)) out.push({ field: "images", message: `Gallery image ${i + 1} needs an https address.` });
      if (typeof x?.alt !== "string" || !x.alt.trim() || x.alt.length > 200) out.push({ field: "images", message: `Gallery image ${i + 1} needs a description.` });
    });
  }
  if (kind === "testimonial") {
    if (!text(data.quote) || text(data.quote).length > 1000) out.push({ field: "quote", message: "The testimonial needs a quote of up to 1000 characters." });
    if (!text(data.author) || text(data.author).length > 120) out.push({ field: "author", message: "The testimonial needs an author." });
    if (text(data.role).length > 120) out.push({ field: "role", message: "A role is at most 120 characters." });
  }
  return out;
}

/** What a condition is tested against for one proposal: the client's industry, tags (labels and ids) and country, the proposal's
 *  value in major units as the proposal stores it, and the name of the package the client picked, if any. */
export type ConditionFacts = { industry?: string; tags: string[]; country?: string; value: number; chosenPackage?: string };

/** Whether a condition holds. Text is compared without case: eq and neq match a whole value, includes matches a part. gt and lt
 *  compare numbers and never hold for text. With no value to test, eq and includes fail and neq holds. */
export function evaluateCondition(c: ProposalCondition, f: ConditionFacts): boolean {
  if (c.field === "proposal.value") return compareNumber(c.op, f.value, Number(c.value));
  const values: (string | undefined)[] = c.field === "client.industry" ? [f.industry] : c.field === "client.country" ? [f.country] : c.field === "package.chosen" ? [f.chosenPackage] : f.tags;
  return matchText(c.op, values.filter((v): v is string => typeof v === "string" && v !== ""), c.value);
}

function compareNumber(op: ProposalCondition["op"], actual: number, target: number): boolean {
  switch (op) {
    case "eq": return actual === target;
    case "neq": return actual !== target;
    case "gt": return actual > target;
    case "lt": return actual < target;
    default: return false;
  }
}

function matchText(op: ProposalCondition["op"], values: string[], value: string | number): boolean {
  const target = String(value).trim().toLowerCase();
  const seen = values.map((v) => v.trim().toLowerCase());
  switch (op) {
    case "eq": return seen.includes(target);
    case "neq": return !seen.includes(target);
    case "includes": return seen.some((v) => v.includes(target));
    default: return false;
  }
}

/** Whether a section is returned by the public GET. A condition on the package is returned with it (the page decides once the
 *  client picks a package); every other condition is applied here. */
export function shownOnServer(data: Record<string, string | number | boolean>, f: ConditionFacts): boolean {
  const c = sectionConditionOf(data);
  if (!c || c.field === "package.chosen") return true;
  return evaluateCondition(c, f);
}

/** A priced line in a pricing section. `interval` is set for recurring lines; an `optional` line can be left out by the client. */
export const PROPOSAL_ITEM = BASE.extend({
  sectionId: z.string(),
  serviceId: z.string().optional(),
  /** The line's own name and note, for lines that are not a catalogue service (add-ons, rate-card hours, one-offs). */
  name: z.string().default(""),
  description: z.string().optional(),
  /** Position in its section. */
  order: z.number().default(0),
  qty: z.number(),
  unit: MONEY,
  interval: z.enum(["month", "quarter", "year"]).optional(),
  optional: z.boolean(),
  selected: z.boolean(),
  /** The workspace tax rate the line is priced with (X20-09b, the quick quote's tax column). Absent for an untaxed line. */
  taxRuleId: z.string().max(200).optional(),
});
export type ProposalItem = z.infer<typeof PROPOSAL_ITEM>;

export const SIGNER_STATUS = z.enum(["pending", "viewed", "signed", "declined"]);
export type SignerStatus = z.infer<typeof SIGNER_STATUS>;

/** A person who must sign an agreement. `contactId` is the client contact; `personId` is the agency staff member who countersigns
 *  (a signer has one or the other). `order` is the signing order. */
export const SIGNER = BASE.extend({
  agreementId: z.string(),
  contactId: z.string().optional(),
  personId: z.string().optional(),
  order: z.number(),
  role: z.string(),
  status: SIGNER_STATUS,
  signedAt: ISO.optional(),
  /** X18-E06: signers with the same group sign in parallel; groups run in ascending order. Missing on older signers (their order is used). */
  group: z.number().int().min(1).optional(),
  /** X18-E06: hours to wait after the previous group's last signature before this group is asked (0 to 720). */
  delayHours: z.number().int().min(0).max(720).optional(),
  /** X18-E06: the signer is skipped while this route condition is false (FIELD_CONDITION, X18-E02). */
  routeIf: z.unknown().optional(),
  /** X18-E06: the earliest time this signer is asked, set when a delayed group is due. */
  askAfter: ISO.optional(),
  /** X18-E06: what the signer may do. Missing means sign only (older signers). */
  can: z.object({ sign: z.boolean(), approve: z.boolean(), edit: z.boolean(), attach: z.boolean() }).partial().optional(),
  /** The Stripe Identity verification session for this signer (its id only; the document never reaches us). */
  identitySession: z.string().optional(),
  /** The session's status as Stripe reports it: requires_input, processing, verified or canceled. */
  identityStatus: z.string().optional(),
  /** How the check runs: "stripe" (Stripe Identity) or "local" (the workspace's own document, MRZ, face and liveness check). */
  identityMethod: z.enum(["stripe", "local"]).optional(),
  /** The local check's evidence: scores, MRZ fields (never the full document number) and blob hashes. Images live in the blob store. */
  identityEvidence: z.object({
    mrz: z.object({ documentType: z.string(), country: z.string(), documentNumberLast4: z.string(), birthDate: z.string(), expiry: z.string(), valid: z.boolean() }).partial().optional(),
    faceScore: z.number().optional(),
    liveness: z.object({ challenge: z.array(z.string()), passed: z.boolean(), stepsPassed: z.number().int().optional() }).optional(),
    documentBlob: z.string().optional(),
    selfieBlob: z.string().optional(),
    attempts: z.number().int().optional(),
    review: z.object({ by: z.string(), at: ISO, decision: z.enum(["approved", "rejected"]), note: z.string().max(500) }).optional(),
  }).optional(),
  /** The answers this signer gave to the document's fields, keyed by field id (signature images as a SHA-256 only). */
  responses: z.record(z.string(), z.string()).optional(),
  /** A5c: the hash of the private blob that keeps the drawn signature PNG (never served). */
  imageFileId: z.string().optional(),
  /** A5c: per signature or initials field, the hash of the private blob that keeps that drawn image. Its response is left empty. */
  imageFileIds: z.record(z.string(), z.string()).optional(),
});
export type Signer = z.infer<typeof SIGNER>;

/** X18-E06 role presets: what each role may do. Reviewer views and comments, so it has no signing or approval right. */
export const SIGNER_ROLE_PRESETS = {
  Signatory: { sign: true, approve: false, edit: false, attach: false },
  Approver: { sign: false, approve: true, edit: false, attach: false },
  Reviewer: { sign: false, approve: false, edit: false, attach: false },
  Countersignatory: { sign: true, approve: false, edit: false, attach: false },
} as const;

/** A signature image as the signing pages send it: a PNG or JPG data URL, base64 text only, at most 300,000 characters. The server hashes it and keeps only the hash. */
export const SIGNATURE_IMAGE = z.string().max(300_000, "The signature image is too large.").regex(/^data:image\/(png|jpeg);base64,[A-Za-z0-9+/]+={0,2}$/, "Use a PNG or JPG image for the signature.");
/** X22-SC-01: where the signer was when signing (city and country) and their time zone. Optional on the server; the signing page asks for both. */
export const SIGN_PLACE = z.strictObject({ city: z.string().min(1).max(80), country: z.string().min(1).max(80) });
export const SIGN_TIMEZONE = z.string().min(1).max(64);

/** A question the client answers when they accept a proposal. A `choice` question names its `options`; a `file` answer is the
 *  id of a file in the workspace. */
export const INTAKE_FIELD = z.object({
  id: z.string().min(1).max(60),
  label: z.string().trim().min(1).max(120),
  kind: z.enum(["text", "choice", "date", "file"]),
  required: z.boolean(),
  options: z.array(z.string().trim().min(1).max(120)).max(20).optional(),
}).refine((f) => f.kind !== "choice" || (f.options?.length ?? 0) > 0, { message: "A choice question needs at least one option.", path: ["options"] });
/** One answer as the client gives it: the question's id and the value (the text, the chosen option, or a file id). */
export const INTAKE_ANSWER = z.object({ fieldId: z.string().min(1).max(60), value: z.string().max(2000) });
/** An answer as the acceptance keeps it: the question as it was asked, so a later edit to the questions changes nothing. */
export const INTAKE_STORED = INTAKE_ANSWER.extend({ label: z.string().max(120) });
export type IntakeField = z.infer<typeof INTAKE_FIELD>;
export type IntakeAnswer = z.infer<typeof INTAKE_STORED>;

export const PROPOSAL_STATUS = z.enum(["draft", "in-approval", "sent", "opened", "accepted", "declined", "expired"]);
/** The look of the public page (the editor's themes). */
export const PROPOSAL_THEME = z.enum(["standard", "warm", "quiet"]);
/** A kept state of a proposal or an agreement draft (X18-E20). `body` is the proposal's sections and items, or the
 *  agreement's blocks and variables. At most 30 are kept; `n` counts up for the record. */
export const DOCUMENT_VERSION = z.object({
  n: z.number().int().min(1),
  at: z.string(),
  by: z.string().nullable(),
  reason: z.enum(["send", "close", "restore", "manual"]),
  body: z.unknown(),
});
export const PROPOSAL = BASE.extend({
  title: z.string(),
  clientId: z.string(),
  dealId: z.string().optional(),
  /** What happens when the client signs (PHASED-PLAN L4): the work template the project is built from and the
   *  onboarding template the client goes through. Either may be empty; the default workflow then skips that step. */
  workTemplateId: z.string().optional(),
  onboardingTemplateId: z.string().optional(),
  /** The gallery template this proposal was made from; the template's win rate counts the proposals made from it. */
  proposalTemplateId: z.string().optional(),
  ownerId: z.string(),
  status: PROPOSAL_STATUS,
  value: MONEY,
  recurring: MONEY.optional(),
  sent: ISO.optional(),
  expires: ISO.optional(),
  opens: z.number(),
  minutesRead: z.number(),
  marginPct: z.number(),
  theme: PROPOSAL_THEME.optional(),
  /** Percent off the chosen one-off total (0 to 100). The agreed value and the deposit use the discounted total. */
  discount: z.number().min(0).max(100).optional(),
  /** The tax rule the deposit invoice is priced with; its tax is added as a line on that invoice. Null when there is none. */
  taxRuleId: z.string().nullable().optional(),
  /** The answers to the custom fields of the template the proposal was made from (X20-09b), keyed by the field's key. */
  fieldValues: z.record(z.string().max(60), z.union([z.string().max(20_000), z.number(), z.boolean()])).optional(),
  /** When the one reminder for an unopened proposal went out. */
  reminded: ISO.optional(),
  /** The video introduction at the top of the public proposal: an https address to embed, and an optional poster image. */
  introVideoUrl: z.string().max(500).regex(/^https:\/\/\S+$/, "Use an https address.").nullable().optional(),
  introVideoPoster: z.string().max(500).regex(/^https:\/\/\S+$/, "Use an https address.").nullable().optional(),
  /** The questions the client answers when they accept (text, a choice or a file). */
  intake: z.array(INTAKE_FIELD).max(20).nullable().optional(),
  /** The answers given at acceptance, each with its question's label as it was asked. */
  intakeAnswers: z.array(INTAKE_STORED).max(20).nullable().optional(),
  /** One entry per recipient link (each contact gets their own link when the proposal is sent): who it was sent to and what they
   *  read. Staff see it; the public proposal never returns it (X7-14a). */
  viewers: z.array(z.object({
    tokenId: z.number().int(), contactId: z.string(), name: z.string(), email: z.string(),
    opens: z.number().int().min(0), minutes: z.number().min(0), lastOpenedAt: ISO.optional(),
  })).max(50).optional(),
  /** Where the proposal came from: the tool it was imported from and its id there (imports only, X4-04). */
  externalRef: z.object({ source: z.string(), id: z.string() }).optional(),
  /** Kept states of the proposal (X18-E20): taken on send, before a draft is saved over, and before a restore. */
  versions: z.array(DOCUMENT_VERSION).max(30).optional(),
});
export type Proposal = z.infer<typeof PROPOSAL>;

/** A saved section in the content library (owners and admins only). `data` holds the body text and the kind's settings. */
export const PROPOSAL_BLOCK = BASE.extend({
  name: z.string().min(1).max(200),
  kind: PROPOSAL_SECTION_KIND,
  title: z.string().min(1).max(200),
  data: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])),
  /** Labels the library is searched by: at most 12, each 1 to 40 characters. */
  tags: z.array(z.string().trim().min(1).max(40)).max(12).optional(),
});
export type ProposalBlock = z.infer<typeof PROPOSAL_BLOCK>;

/** A credit against an issued invoice's unpaid balance (a discount after the fact, a goodwill credit, a correction). */
export const CREDIT_NOTE = BASE.extend({
  number: z.string(),
  invoiceId: z.string(),
  clientId: z.string(),
  amount: MONEY,
  reason: z.string(),
  issued: ISO,
  issuedBy: z.string(),
});
export type CreditNote = z.infer<typeof CREDIT_NOTE>;

/** The values of a block's optional style fields. Each is a key of a fixed map in agreements.ts (renderBlocks), never raw CSS. */
export const AGREEMENT_SIZES = ["s", "m", "l", "xl"] as const;
export const AGREEMENT_TONES = ["default", "primary", "muted", "accent", "danger"] as const;
export const AGREEMENT_ALIGNS = ["left", "center", "right"] as const;

/** One block of an agreement's document: a heading, a paragraph, a clause copied from the library, or the signature area (where
 *  the signatures sit; without one they render at the end). `text` may hold {{variables}}; a clause keeps the library id it was
 *  copied from. size, tone and align style any block; level (1 is the largest) styles a heading. A block without them renders as before. */
export const AGREEMENT_BLOCK = z.object({
  id: z.string(),
  kind: z.enum(["heading", "text", "clause", "signature"]),
  text: z.string(),
  clauseId: z.string().optional(),
  size: z.enum(AGREEMENT_SIZES).optional(),
  tone: z.enum(AGREEMENT_TONES).optional(),
  align: z.enum(AGREEMENT_ALIGNS).optional(),
  level: z.union([z.literal(1), z.literal(2), z.literal(3)]).optional(),
});
export type AgreementBlock = z.infer<typeof AGREEMENT_BLOCK>;

/** A comment on a block, or a change proposed for it (by the agency or the client), with every decision on it in `history`. */
export const AGREEMENT_SUGGESTION = z.object({
  id: z.string(),
  blockId: z.string(),
  kind: z.enum(["comment", "suggestion"]),
  side: z.enum(["agency", "client"]),
  author: z.string(),
  /** The comment, or the proposed replacement text of the block. */
  text: z.string(),
  status: z.enum(["open", "accepted", "rejected", "resolved"]),
  at: ISO,
  history: z.array(z.object({ at: ISO, by: z.string(), action: z.enum(["commented", "proposed", "accepted", "rejected", "resolved"]), note: z.string().optional() })),
});
export type AgreementSuggestion = z.infer<typeof AGREEMENT_SUGGESTION>;

/** The signed copy received from outside the platform: a file on record, named when it was attached. */
// X22-SC-01: `sealed` is set when the copy carries a PKCS#7 seal that verified at completion (null or absent when it does not)
export const SIGNED_COPY = z.object({ fileId: z.string(), name: z.string(), attachedAt: ISO, sealed: z.object({ signer: z.string(), fingerprint: z.string(), signingTime: ISO }).optional() });

/** The payment plan an agreement was split into: equal instalments from `firstDueOn`, one invoice each (`invoiceIds`).
 *  `milestoneLinks` maps an instalment invoice id to a milestone id: that instalment goes out when the milestone is done, not on its date. */
export const PAYMENT_PLAN = z.object({ count: z.number().int(), intervalDays: z.number().int(), firstDueOn: ISO_DATE, amount: MONEY, invoiceIds: z.array(z.string()), milestoneLinks: z.record(z.string(), z.string()).optional() });

/** A key term of an agreement, shown on the repository and searched with the title (for example "Notice period: 30 days"). */
export const KEY_TERM = z.object({ label: z.string(), value: z.string() });

/** One priced line of an agreement's commercial terms (X7-01e): a rate card row or a typed price. */
export const COMMERCIAL_LINE = z.object({
  rateCardId: z.string().optional(), description: z.string(), qty: z.number(), unit: MONEY, total: MONEY,
  /** Where the unit price came from: "Rate card: designer", "Client rate", "Project rate", "Personal rate" or "typed". */
  source: z.string().optional(),
});
export const COMMERCIAL_TERMS = z.object({ lines: z.array(COMMERCIAL_LINE), total: MONEY });
export type CommercialTerms = z.infer<typeof COMMERCIAL_TERMS>;

export const AGREEMENT = BASE.extend({
  title: z.string(),
  kind: z.enum(["msa", "sow", "nda", "change-request"]),
  /** The agency's agreement type (X10-62a, a list entry in settings agreementTypes); its kind must equal `kind`. */
  typeId: z.string().optional(),
  clientId: z.string(),
  status: z.enum(["draft", "out-for-signature", "signed", "expired", "declined", "voided"]),
  signers: z.object({ signed: z.number(), total: z.number() }),
  value: MONEY.optional(),
  renews: ISO.optional(),
  parentId: z.string().optional(),
  /** X18-E08: the person who owns the agreement. */
  ownerId: z.string().optional(),
  /** X18-E08: people the owner shared the agreement with; `view` reads it, `edit` may also change it while it is a draft. */
  sharedWith: z.array(z.strictObject({ personId: z.string().min(1).max(200), access: z.enum(["view", "edit"]) })).optional(),
  /** X18-E08: set when the agreement is in the trash. Trashed agreements are hidden from lists until restored. */
  trashedAt: ISO.optional(),
  /** X18-E08: the voided agreement this draft was made from by a correction. */
  correctsId: z.string().optional(),
  /** X18-E08: set when the agreement is voided: the day, and the reason the sender gave. */
  voidedAt: ISO.optional(),
  voidReason: z.string().max(500).optional(),
  /** X18-E08: the reason a signer gave when declining to sign. */
  declinedReason: z.string().max(500).optional(),
  /** The accepted proposal this statement of work came from. */
  proposalId: z.string().optional(),
  /** The document, in order (PHASED-PLAN F8). Changed only while the agreement is a draft, or by an accepted change before any signature. */
  blocks: z.array(AGREEMENT_BLOCK).optional(),
  /** Values for the document's {{variables}} that the agreement sets itself; built-in ones come from the client and the proposal. */
  variables: z.record(z.string(), z.string()).optional(),
  /** Comments and proposed changes on blocks, with their history. */
  suggestions: z.array(AGREEMENT_SUGGESTION).optional(),
  /** The date after which the agreement can no longer be signed. */
  expiresAt: ISO.optional(),
  /** Whether signers must enter a passcode. The passcode itself is sealed in the vault, never kept in the record. */
  passcodeRequired: z.boolean().optional(),
  /** The deposit this agreement asks for, as a percentage of its value, raised when it is signed. Unset or null: no deposit. */
  depositPct: z.number().min(0).max(100).nullable().optional(),
  /** How signers receive their six-digit code: by email (default) or by text through the workspace's Twilio number. */
  signerVerification: z.enum(["email", "sms"]).optional(),
  /** Whether each signer must complete an identity check (Stripe Identity, document and selfie) before the server accepts their signature. */
  identityCheck: z.boolean().optional(),
  /** The signed copy received from outside the platform. */
  signedCopy: SIGNED_COPY.optional(),
  /** Set when the agreement was signed outside the platform: the day it was signed, who recorded it, and a note. */
  signedExternally: z.object({ signedOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/), by: z.string(), recordedAt: ISO, note: z.string().max(500).optional() }).optional(),
  /** The payment plan the agreement was split into. */
  paymentPlan: PAYMENT_PLAN.optional(),
  /** Priced lines picked from the rate card or typed; their total is the agreement's value (X7-01e). */
  commercial: COMMERCIAL_TERMS.optional(),
  /** Key terms shown on the repository. */
  keyTerms: z.array(KEY_TERM).optional(),
  /** The deposit invoice raised when the proposal was accepted; its pay link is shown right after the last signature. */
  depositInvoiceId: z.string().optional(),
  /** SHA-256 of the document as it was sent (and again when an accepted change alters it before any signature). */
  documentSha256: z.string().optional(),
  /** The fields a signer fills inside the document (X18-E01). Changed only while the agreement is a draft. */
  fields: z.array(ESIGN_FIELD).max(100).optional(),
  /** A5a: the uploaded PDF (files API id) the agreement's positional fields sit on. Unset: the agreement is an HTML document. */
  pdfFileId: z.string().optional(),
  /** Kept states of the document (X18-E20): taken on send, before a draft's text is saved over, and before a restore. */
  versions: z.array(DOCUMENT_VERSION).max(30).optional(),
  /** The bulk send batch this agreement was made in (X18-E10). */
  bulkId: z.string().optional(),
});
export type Agreement = z.infer<typeof AGREEMENT>;

/** A saved agreement document, started again for any client (X7-14c). Clauses keep the library id they came from, but the
 *  text in a template is a copy, so a later library edit does not change it. No signers, expiry or passcode are kept. */
export const AGREEMENT_TEMPLATE = BASE.extend({
  name: z.string().min(1).max(200),
  kind: z.enum(["msa", "sow", "nda", "change-request"]),
  /** The agency's agreement type (X10-62a); copied into a draft from the template while the type is live. */
  typeId: z.string().optional(),
  blocks: z.array(AGREEMENT_BLOCK),
  variables: z.record(z.string(), z.string()).optional(),
  keyTerms: z.array(KEY_TERM).optional(),
  uses: z.number().int().min(0),
});
export type AgreementTemplate = z.infer<typeof AGREEMENT_TEMPLATE>;

/** A renewal cadence (X18-I01a): a named interval, or every N days, weeks or months. */
export type Cadence = "week" | "two-weeks" | "month" | "quarter" | "year" | { every: number; unit: "day" | "week" | "month" };

const DAY_MS = 86_400_000;

/** Whole months from an ISO time. The day of the month is kept, or the month's last day when the month is shorter, and a
 *  renewal on a month's last day stays on the last day (31 Aug, 30 Sep, 31 Oct). The same arithmetic the billing job uses. */
function addMonthsIso(iso: string, n: number): string {
  const d = new Date(iso);
  const lastOf = (y: number, m: number) => new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
  const day = d.getUTCDate() === lastOf(d.getUTCFullYear(), d.getUTCMonth()) ? 31 : d.getUTCDate();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() + n);
  d.setUTCDate(Math.min(day, lastOf(d.getUTCFullYear(), d.getUTCMonth())));
  return d.toISOString();
}

/** The cadence a plan record renews on: its named interval, or its custom count. A custom plan with no count bills monthly. */
export function cadenceOf(plan: { interval?: unknown; customCadence?: unknown }): Cadence {
  if (plan.interval === "custom") {
    const c = plan.customCadence as { every?: number; unit?: "day" | "week" | "month" } | null | undefined;
    return c?.every && c.unit ? { every: c.every, unit: c.unit } : { every: 1, unit: "month" };
  }
  return (typeof plan.interval === "string" ? plan.interval : "month") as Cadence;
}

const cadenceUnit = (c: Cadence): [number, "day" | "week" | "month"] =>
  typeof c === "string" ? (c === "week" ? [1, "week"] : c === "two-weeks" ? [2, "week"] : [c === "quarter" ? 3 : c === "year" ? 12 : 1, "month"]) : [c.every, c.unit];

/** The start of the period one step from `iso`: forward (n = 1) or back (n = -1). Every period advance goes through here. */
export function shiftPeriod(iso: string, cadence: Cadence, n: 1 | -1): string {
  const [every, unit] = cadenceUnit(cadence);
  if (unit === "day") return new Date(Date.parse(iso) + n * every * DAY_MS).toISOString();
  if (unit === "week") return new Date(Date.parse(iso) + n * every * 7 * DAY_MS).toISOString();
  return addMonthsIso(iso, n * every);
}
/** The start of the period after one that starts at `start`. */
export const nextPeriodStart = (start: string, cadence: Cadence): string => shiftPeriod(start, cadence, 1);
/** The start of the period before one that ends at `end`. */
export const previousPeriodStart = (end: string, cadence: Cadence): string => shiftPeriod(end, cadence, -1);
/** Months in one period, for prices and monthly equivalents: a week is 12/52 of a year. */
export function periodMonths(cadence: Cadence): number {
  const [every, unit] = cadenceUnit(cadence);
  return unit === "day" ? (every * 12) / 365 : unit === "week" ? (every * 12) / 52 : every;
}
/** True when two cadences renew at the same rhythm. */
export const sameCadence = (a: Cadence, b: Cadence): boolean =>
  typeof a === "string" || typeof b === "string" ? a === b : a.every === b.every && a.unit === b.unit;

export const SUBSCRIPTION = BASE.extend({
  name: z.string(),
  clientId: z.string(),
  status: z.enum(["active", "paused", "past-due", "cancelled", "trial", "ended"]),
  mrr: MONEY,
  renews: ISO,
  /** Retainer hours of the current period. `used` is computed by the billing job from the client's time; `alertedPct` is the
   *  highest burn alert (75, 80, 90) already sent this period. */
  hours: z.object({ used: z.number(), included: z.number(), rollover: z.number(), alertedPct: z.number().optional() }).optional(),
  interval: z.enum(["week", "two-weeks", "month", "quarter", "year", "custom"]),
  /** The custom renewal cadence: every N days, weeks or months. Set when `interval` is "custom" (X18-I01a). */
  customCadence: z.object({ every: z.number().int().min(1).max(365), unit: z.enum(["day", "week", "month"]) }).nullable().optional(),
  /** The last day a period may start on: no period starts after it, and the plan ends there (X18-I01a). */
  endsOn: ISO_DATE.nullable().optional(),
  /** Stop after this many invoices (X18-I01a). `invoicesBilled` counts the renewal invoices billed so far. */
  endsAfter: z.number().int().min(1).max(1000).nullable().optional(),
  invoicesBilled: z.number().int().min(0).optional(),
  /** Each renewal is raised as a draft to review and send from the Drafts list, not sent at once (X18-I01a). */
  reviewFirst: z.boolean().optional(),
  /** Days until this plan's invoices are due. Null or unset follows the client's billing profile, then the workspace terms (X6-08a). */
  termsDays: z.number().int().min(0).max(365).nullable().optional(),
  /** The package this plan was sold as, when it came from the storefront or a quote. */
  packageId: z.string().optional(),
  /** The bank reference a client writes on each transfer for this plan (X18-I07). Set the first time a renewal is raised; the pay instructions show it and the bank matcher finds it. */
  reference: z.string().trim().min(4).max(24).optional(),
  /** The accepted proposal that started this plan; the accept workflow looks it up to stay safe to repeat. */
  proposalId: z.string().optional(),
  /** What one interval costs (the amount billed and prorated); `mrr` is its monthly equivalent. */
  price: MONEY.optional(),
  /** Retainer rules (PHASED-PLAN L12): what happens to hours over the allowance at the end of a period, how many unused
   *  hours carry over, and the burn alert levels (percent of the allowance). `alerted` is the highest custom level already
   *  sent for the period that ended at `alertedFor`. */
  retainer: z.object({
    overage: z.enum(["bill", "carry", "absorb"]),
    overageRate: MONEY.optional(),
    autoSend: z.boolean().optional(),
    rolloverCapHours: z.number().min(0).optional(),
    thresholds: z.array(z.number().min(1).max(500)).optional(),
    alerted: z.number().optional(),
    alertedFor: ISO.optional(),
  }).optional(),
  /** A plan change that takes effect at the next renewal (a downgrade). */
  pending: z.object({
    name: z.string(),
    price: MONEY,
    mrr: MONEY,
    interval: z.enum(["week", "two-weeks", "month", "quarter", "year", "custom"]),
    customCadence: z.object({ every: z.number().int().min(1).max(365), unit: z.enum(["day", "week", "month"]) }).nullable().optional(),
    packageId: z.string().optional(),
    effective: ISO,
  }).optional(),
});
export type Subscription = z.infer<typeof SUBSCRIPTION>;
